import {
  type SDK_Align,
  type SDK_Blank,
  type SDK_Filter,
  SDK_FontStyle,
  SDK_FontWeight,
  type SDK_Fonts,
  SDK_StartingPoint,
  SDK_TextDecoration,
  strings
} from "@quickpoint.me/shared";

import type { DefaultTheme } from "styled-components";

// type Align = "flex-start" | "flex-end" | "center" | strings.unset;
type Decoration = "overline" | "underline" | "line-through" | typeof strings.unset;
type GridPosition = "end" | "center" | "start" | typeof strings.unset;
type Style = "italic" | "normal" | typeof strings.unset;
type Weight = "normal" | "bold" | "lighter" | "bolder" | typeof strings.unset;
// account for overflowing flex container on mobile
type VerticalGridPosition = "justify-content:flex-end" | "justify-content:center" | "justify-content:flex-start" | "justify-content:unset" | "margin-block-end:auto; margin-block-start:auto";


export function fontStyle(style: SDK_FontStyle | null | undefined): Style {
  switch (style) {
    case undefined:
      return strings.unset;
    case SDK_FontStyle.Italic:
      return "italic";
    case SDK_FontStyle.Normal:
      return "normal";
  }

  return strings.unset;
}

export function fontWeight(weight: SDK_FontWeight | undefined | null): Weight {
  switch (weight) {
    case undefined:
      return strings.unset;
    case SDK_FontWeight.Bold:
      return "bold";
    case SDK_FontWeight.Bolder:
      return "bolder";
    case SDK_FontWeight.Lighter:
      return "lighter";
    case SDK_FontWeight.Normal:
      return "normal";
  }

  return strings.unset;
}

export const fontSize = (theme: SDK_Filter | SDK_Blank, baseFontSize: number, multiplier = 1) => {
  if (theme.fontSize) {
    const size = theme.fontSize as unknown as number;
    return `${size * baseFontSize * multiplier}px`;
  }
  return strings.unset;
};

export const font = (theme: DefaultTheme, fonts: SDK_Fonts | undefined) => {
  if (!fonts) return strings.unset;
  const font = fonts[theme.font];
  return font ? `"${font}"` : strings.unset;
};



export function elementAlign(align: SDK_Align | undefined | null, $isInFlexBox: boolean): string {
  if (align) {
    if ($isInFlexBox) return `align-self: ${align}`
    return `justify-self: ${align}`
  }
  return "";
}

export function startingPoint(
  position: SDK_StartingPoint | undefined | null,
  isOverflowing?: boolean | undefined,
  _inLandscape?: boolean | undefined,
  forceSingleColumn?: boolean | undefined
): GridPosition | VerticalGridPosition {
  switch (position) {
    case SDK_StartingPoint.BottomUp:
      return "end";
    case SDK_StartingPoint.MiddleOut:
      // WCAG 1.4.10: When forceSingleColumn is true, start from top to allow scrolling
      if (forceSingleColumn) return "start";
      return isOverflowing ? "start" : "center";

    case SDK_StartingPoint.TopDown:
      return "start";
  }

  return strings.unset;
}

export function textDecoration(
  decoration: SDK_TextDecoration | undefined | null,
): Decoration {
  switch (decoration) {
    case SDK_TextDecoration.Strikethrough:
      return "line-through";
    case SDK_TextDecoration.Underline:
      return "underline";
  }
  return strings.unset;
}

export function opacityHelper(tapped: boolean, opacityValue?: number | string | null,): number {
  if (!opacityValue) return 1;
  if (typeof opacityValue === "string") {
    opacityValue = Number.parseInt(opacityValue);
  }
  const op = opacityValue ? opacityValue / 100 : 1;
  return tapped ? op : 0;
}
/** Filter themes store color CHOICES ("first"…"eighth"); anything painting
 * them directly must resolve against the palette or the CSS value is the
 * literal word and the paint silently fails. */
const COLOR_CHOICES = new Set([
  "first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth",
]);

export function resolveColorChoice(
  value: string | null | undefined,
  colors: Record<string, string | undefined | null> | undefined,
): string {
  if (!value) return "unset";
  const resolved = colors?.[value];
  if (typeof resolved === "string") return resolved;
  // a palette slot the filter doesn't define must not become invalid CSS
  return COLOR_CHOICES.has(value) ? "unset" : value;
}
